import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import axiosConn from '../../axiosConn';
import AuthChooser from '../../components/auth/AuthChooser';
import JGLocalOpsMark from '../../components/common/JGLocalOpsMark';
import GenericHelmet from '../../components/genericHelmet/GenericHelmet';
import { AuthContext } from '../../context/AuthContext';
import {
  COLLISION_SPECIALIST_PICKUP_ADDRESS,
  COLLISION_SPECIALIST_RIDE_INTENT,
  COLLISION_SPECIALIST_RIDE_SOURCE,
  COLLISION_SPECIALIST_RIDE_TEMPLATE,
  isCollisionSpecialistRideRequest,
} from '../../utils/localOpsCollisionSpecialistRide';
import {
  getLocalOpsLaunchSourceTag,
  LAUNCH_VISIT_EVENTS,
  trackLocalOpsLaunchEvent,
} from '../../utils/localOpsLaunchEvents';
import {
  getGoogleOAuthStartUrl,
  isGoogleOAuthEnabled,
} from '../../utils/googleOAuth';
import './localOpsRegionLanding.css';

const AUTH_BASE = process.env.REACT_APP_AUTH_BASE_URL || '';
const JG_LOCALOPS_SHARE_IMAGE =
  'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1664565843/Images/JOEGALAXYDopeGalaxyLogooriginal_tecmer.png';
const CAMDEN_CANONICAL_URL = 'https://jgops.io/camden';
const CS_RIDE_CANONICAL_URL = 'https://jgops.io/cs-ride';
const CAMDEN_HELMET_TITLE =
  'JG LocalOps Camden | Local Tasks, Rides, Errands & Helpers';
const CAMDEN_HELMET_DESCRIPTION =
  'Post local tasks, request ride help, run errands, or earn by helping people nearby in Camden, TN. Verified completions build Action Credit.';
const CS_RIDE_HELMET_TITLE =
  'Ride Help from Collision Specialist | JG LocalOps Camden';
const CS_RIDE_HELMET_DESCRIPTION =
  'Dropped off your car at Collision Specialist? Request local ride help from 134 West Main Street in Camden, TN. Sign in with email, fund the request, and nearby helpers can accept.';

const normalizeSlug = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  return raw
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');
};

const toRegionDisplayName = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(' ');
};

const buildDefaultRegionCanonicalUrl = (slug) =>
  slug
    ? `https://www.joegalaxy.net/localops/region/${encodeURIComponent(slug)}`
    : 'https://www.joegalaxy.net/localops';

const buildRegionHelmet = ({
  isCollisionSpecialistRide,
  regionLabel,
  slug,
}) => {
  if (isCollisionSpecialistRide) {
    return {
      title: CS_RIDE_HELMET_TITLE,
      description: CS_RIDE_HELMET_DESCRIPTION,
      canonicalUrl: CS_RIDE_CANONICAL_URL,
      image: JG_LOCALOPS_SHARE_IMAGE,
      url: CS_RIDE_CANONICAL_URL,
      regionName: 'Camden, TN',
      ogTitle: 'Ride Help from Collision Specialist',
      ogDescription:
        'Request local ride help after dropping off your car in Camden, TN.',
      ogUrl: CS_RIDE_CANONICAL_URL,
      twitterCard: 'summary_large_image',
    };
  }

  if (slug === 'camden') {
    return {
      title: CAMDEN_HELMET_TITLE,
      description: CAMDEN_HELMET_DESCRIPTION,
      canonicalUrl: CAMDEN_CANONICAL_URL,
      image: JG_LOCALOPS_SHARE_IMAGE,
      url: CAMDEN_CANONICAL_URL,
      regionName: 'Camden, TN',
      ogTitle: 'JG LocalOps Camden',
      ogDescription: 'Local ride help, errands, pickups, and paid tasks in Camden, TN.',
      ogUrl: CAMDEN_CANONICAL_URL,
      twitterCard: 'summary_large_image',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'JG LocalOps Camden',
        areaServed: 'Camden, TN',
        url: CAMDEN_CANONICAL_URL,
        description:
          'Local help, paid tasks, errands, and verified contribution in Camden, TN.',
      },
    };
  }

  const safeRegionLabel = regionLabel || 'Local region';
  const canonicalUrl = buildDefaultRegionCanonicalUrl(slug);
  const description = `Local help, paid tasks, errands, and verified contribution in ${safeRegionLabel}.`;

  return {
    title: `JG LocalOps ${safeRegionLabel}`,
    description,
    canonicalUrl,
    image: JG_LOCALOPS_SHARE_IMAGE,
    url: canonicalUrl,
    regionName: safeRegionLabel,
    ogTitle: `JG LocalOps ${safeRegionLabel}`,
    ogDescription: description,
    ogUrl: canonicalUrl,
    twitterCard: 'summary_large_image',
  };
};

const INTENTS = {
  HELP: 'help',
  EARN: 'earn',
  EXPLORE: 'explore',
  PARTNER: 'partner',
  FIRESTARTER: 'firestarter',
};

const PARTNER_ACTIONS = {
  POST_TASK: 'post_task',
  RECURRING_HELP: 'recurring_help',
  SPONSOR_OPPORTUNITIES: 'sponsor_opportunities',
};

const PUBLIC_LAUNCH_USE_ROWS = [
  'Need help moving a couch 2 miles',
  'Post a grocery delivery errand',
  'Find or create same-day neighborhood tasks',
];
const CAMDEN_PAGE_HEADLINE = 'JG LocalOps Camden';
const CAMDEN_PAGE_TRUST_LINE =
  'Built in Camden. Family-run. Camden people helping Camden people.';
const CAMDEN_PAGE_SUBCOPY =
  'Get help, earn nearby, or involve your business. Rides, errands, pickups, deliveries, and small paid tasks handled locally.';
const CAMDEN_PAGE_SECONDARY_TRUST =
  'A local-first way to keep help, trust, and opportunity in Camden.';
const CS_RIDE_HEADLINE = 'Need a ride after dropping off your car?';
const CS_RIDE_SUBCOPY =
  'JG LocalOps was built in Camden and is family-run. A local helper can accept your ride request from Collision Specialist.';
const CS_RIDE_TRUST_BULLETS = [
  'Pickup is locked at Collision Specialist.',
  'Destination stays private until a helper accepts.',
  'Payout is reserved before the ride is posted.',
  'Live tracking helps everyone feel safe.',
  'Camden people helping Camden people.',
];
const CS_RIDE_HELP_PHONE = '+13057637749';
const CS_RIDE_HELP_PHONE_LABEL = '305-763-7749';
const CS_RIDE_SMS_BODY =
  'I need a ride from Collision Specialist in Camden.\nDestination:\nName:';

const FIRESTARTER_MODE = 'task-first-firestarter-v1';
const LAUNCH_UPDATE_CONSENT_TEXT_VERSION = 'localops-region-launch-updates-v1';
const LAUNCH_UPDATE_SUCCESS_COPY = "Thanks. You're on the Camden launch updates list.";
const LOCALOPS_SPOTLIGHT_SURFACE_KEY = 'localops_region';

const AUTH_CONTEXT = {
  NONE: '',
  HELP: 'help',
  EARN: 'earn',
  FIRESTARTER: 'firestarter',
  RIDE: COLLISION_SPECIALIST_RIDE_INTENT,
};

const LIVE_PROOF_FALLBACK_COPY =
  'Local tasks are already being completed in Camden.';

const PATH_INTENT_COPY = {
  [INTENTS.HELP]: 'Post a paid task. A nearby helper can accept.',
  [INTENTS.EARN]:
    'Find local tasks, help Camden neighbors, and build verified Action Credit.',
  [INTENTS.PARTNER]:
    'Get local visibility, offers, pickups, errands, or service help through LocalOps.',
  [INTENTS.EXPLORE]:
    'See completed local tasks, proof receipts, and local business activity first.',
};

const DEFAULT_PATH_CARD_ORDER = [
  INTENTS.HELP,
  INTENTS.EARN,
  INTENTS.PARTNER,
  INTENTS.EXPLORE,
];
const CAMDEN_PATH_CARD_ORDER = [
  INTENTS.EARN,
  INTENTS.HELP,
];
const REQUESTER_SOURCE_HINTS = [
  'doctor_flyer',
  'collision_specialist',
  'ride_help',
  'help_flyer',
  'appointment_ride',
  'medical_flyer',
];
const BUSINESS_SOURCE_HINTS = [
  'business_ad',
  'spotlight',
  'merchant',
  'chamber',
  'busby',
  'local_business',
];

const normalizePriorityToken = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');

const sourceHasHint = (source, hints) => {
  const normalized = normalizePriorityToken(source);
  if (!normalized) return false;
  return hints.some((hint) => normalized.includes(hint));
};

export const resolveCamdenLandingPriority = ({
  intent,
  src,
  isCollisionSpecialistRide,
} = {}) => {
  if (isCollisionSpecialistRide) return COLLISION_SPECIALIST_RIDE_INTENT;
  const normalizedIntent = normalizePriorityToken(intent);
  if (normalizedIntent === INTENTS.HELP) return INTENTS.HELP;
  if (normalizedIntent === INTENTS.EARN) return INTENTS.EARN;
  if (normalizedIntent === 'business' || normalizedIntent === INTENTS.PARTNER) {
    return INTENTS.PARTNER;
  }
  if (normalizedIntent === INTENTS.EXPLORE) return INTENTS.EXPLORE;
  if (sourceHasHint(src, REQUESTER_SOURCE_HINTS)) return INTENTS.HELP;
  if (sourceHasHint(src, BUSINESS_SOURCE_HINTS)) return INTENTS.PARTNER;
  return INTENTS.EARN;
};

const buildPathCards = ({ isCamdenRegion }) => [
  {
    intent: INTENTS.HELP,
    title: isCamdenRegion ? 'Get local help' : 'I need help',
    copy: PATH_INTENT_COPY[INTENTS.HELP],
    className: 'localops-region__intent--help',
    ctaLabel: isCamdenRegion ? 'Get help nearby' : 'Get help',
  },
  {
    intent: INTENTS.EARN,
    title: isCamdenRegion ? 'Earn nearby' : 'I want to earn',
    copy: isCamdenRegion
      ? PATH_INTENT_COPY[INTENTS.EARN]
      : 'Find local tasks and build verified Action Credit through completed work.',
    className: 'localops-region__intent--earn',
    badge: isCamdenRegion ? 'Most popular in Camden' : '',
    ctaLabel: isCamdenRegion ? 'Start earning nearby' : 'Find work',
  },
  {
    intent: INTENTS.PARTNER,
    title: isCamdenRegion
      ? 'Grow your Camden business'
      : 'I run a business / organization',
    copy: PATH_INTENT_COPY[INTENTS.PARTNER],
    className: 'localops-region__intent--partner',
    ctaLabel: 'Work with LocalOps',
  },
  {
    intent: INTENTS.EXPLORE,
    title: isCamdenRegion ? 'Browse Camden activity' : "I'm just exploring",
    copy: isCamdenRegion
      ? PATH_INTENT_COPY[INTENTS.EXPLORE]
      : 'Browse local activity before choosing a path.',
    className: 'localops-region__intent--explore',
    ctaLabel: 'Browse activity',
  },
];

const orderPathCards = ({ cards, dominantIntent, isCamdenRegion }) => {
  if (isCamdenRegion) {
    const primaryOrder =
      dominantIntent === INTENTS.PARTNER
        ? [INTENTS.PARTNER, INTENTS.EARN, INTENTS.HELP]
        : dominantIntent === INTENTS.HELP
        ? [INTENTS.HELP, INTENTS.EARN]
        : CAMDEN_PATH_CARD_ORDER;
    return primaryOrder
      .map((intent) => cards.find((card) => card.intent === intent))
      .filter(Boolean);
  }

  const baseOrder = DEFAULT_PATH_CARD_ORDER;
  const preferredOrder =
    dominantIntent && baseOrder.includes(dominantIntent)
      ? [dominantIntent, ...baseOrder.filter((intent) => intent !== dominantIntent)]
      : baseOrder;
  return preferredOrder
    .map((intent) => cards.find((card) => card.intent === intent))
    .filter(Boolean);
};

const HOW_LOCALOPS_WORKS_STEPS = [
  {
    title: 'Choose a local path',
    copy: 'Post a paid task, find work nearby, involve a business, or browse first.',
  },
  {
    title: 'Continue on the map',
    copy: 'LocalOps keeps Camden actions tied to the right region and intent.',
  },
  {
    title: 'Fund or accept work',
    copy: 'Requesters fund paid tasks before publishing. Helpers accept work when available.',
  },
  {
    title: 'Complete and verify',
    copy: 'Completed work can create an Action Proof Receipt and build Action Credit.',
  },
];

const buildPlayerMapReturnTo = ({
  intent,
  slug,
  src,
  isCold = false,
  extraParams = {},
}) => {
  const params = new URLSearchParams();
  params.set('intent', intent);
  params.set('regionSlug', slug);
  params.set('source', 'offline_region');
  if (isCold) params.set('isCold', '1');
  if (src) params.set('src', src);
  Object.entries(extraParams || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.set(key, String(value));
  });
  return `/playermap?${params.toString()}`;
};

const buildLocalOpsContinueReturnTo = (target) =>
  `/localops/continue?returnTo=${encodeURIComponent(target)}`;

const createRequesterStarterAutoOpenId = () => {
  const random = Math.random().toString(36).slice(2, 10);
  const timestamp = Date.now().toString(36);
  return `${timestamp}${random}`.slice(0, 18);
};

const buildRequesterStarterAutoOpenParams = (handoffId) =>
  handoffId
    ? {
        starterAutoOpen: '1',
        starterAutoOpenId: handoffId,
      }
    : {};

const buildSmsHref = (phone, body) =>
  `sms:${phone}?&body=${encodeURIComponent(body)}`;

const compactText = (value, max = 180) => {
  const text = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '';
  return text.length > max ? text.slice(0, max).trim() : text;
};

const toDisplayCount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.floor(parsed);
};

const formatCount = (value) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);

const buildLiveProofStats = (data) => {
  const publicStats = data?.publicStats || {};
  const completedLocalTasks = toDisplayCount(publicStats.completedLocalTasks);
  const verifiedLocals = toDisplayCount(publicStats.verifiedLocals);
  const activeOfferings = toDisplayCount(
    publicStats.activeOfferings ?? data?.regionHealth?.offeringsCount,
  );
  return [
    completedLocalTasks === null
      ? null
      : {
          key: 'completedLocalTasks',
          value: completedLocalTasks,
          label:
            completedLocalTasks === 1
              ? 'completed local task'
              : 'completed local tasks',
        },
    verifiedLocals === null
      ? null
      : {
          key: 'verifiedLocals',
          value: verifiedLocals,
          label: verifiedLocals === 1 ? 'Verified Local' : 'Verified Locals',
        },
    activeOfferings === null
      ? null
      : {
          key: 'activeOfferings',
          value: activeOfferings,
          label: activeOfferings === 1 ? 'active offering' : 'active offerings',
        },
  ].filter(Boolean);
};

const isSafeProofUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (raw.startsWith('/share/localops/proof/')) return true;
  try {
    const parsed = new URL(raw, 'https://www.joegalaxy.net');
    const allowedHosts = new Set([
      'www.joegalaxy.net',
      'joegalaxy.net',
      'api.joegalaxy.net',
      'jgops.io',
      'www.jgops.io',
    ]);
    return (
      allowedHosts.has(parsed.hostname.toLowerCase()) &&
      parsed.pathname.startsWith('/share/localops/proof/')
    );
  } catch (err) {
    return false;
  }
};

const normalizeProofReceipt = (receipt) => {
  if (!receipt || typeof receipt !== 'object') return null;
  const proofUrl = compactText(
    receipt.proofUrl || receipt.sharePath || receipt.url,
    2000,
  );
  if (!isSafeProofUrl(proofUrl)) return null;
  const proofId =
    compactText(receipt.proofId, 120) ||
    compactText(proofUrl.split('/').filter(Boolean).pop(), 120);
  const completedAt = receipt.completedAt ? new Date(receipt.completedAt) : null;
  return {
    proofId,
    proofUrl,
    title: compactText(receipt.title, 120) || 'Completed LocalOps task',
    categoryLabel:
      compactText(receipt.categoryLabel, 80) || 'Action Proof Receipt',
    shortSummary: compactText(receipt.shortSummary, 180),
    completedAt:
      completedAt && !Number.isNaN(completedAt.getTime())
        ? completedAt.toISOString()
        : '',
  };
};

const isSafeSpotlightUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (raw.startsWith('/') && !raw.startsWith('//')) return true;
  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch (err) {
    return false;
  }
};

const isSafeSpotlightImageUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return false;
  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'https:';
  } catch (err) {
    return false;
  }
};

const buildSpotlightAdvertisePath = (slug) =>
  `/localops/region/${encodeURIComponent(slug || 'camden')}/advertise?src=localops_spotlight`;

const trackSpotlightEvent = ({ eventType, regionSlug, token, trackingId }) => {
  const safeTrackingId = compactText(trackingId, 128);
  if (!safeTrackingId || !['impression', 'click'].includes(eventType)) {
    return Promise.resolve(null);
  }
  const path = `local-business-spotlight/public/ads/${encodeURIComponent(
    safeTrackingId,
  )}/${eventType}`;
  const body = {
    surfaceKey: LOCALOPS_SPOTLIGHT_SURFACE_KEY,
    regionSlug: normalizeSlug(regionSlug) || 'camden',
  };
  const config = token
    ? { headers: { Authorization: `Bearer ${token}` } }
    : undefined;
  const request = config
    ? axiosConn.post(path, body, config)
    : axiosConn.post(path, body);
  return request.catch(() => null);
};

const normalizeComparableText = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '')
    .trim();

const isDuplicateSpotlightText = (value, reference) => {
  const candidate = normalizeComparableText(value);
  const base = normalizeComparableText(reference);
  if (!candidate || !base) return false;
  if (candidate === base) return true;
  if (candidate.startsWith(base) && candidate.length <= base.length + 12) {
    return true;
  }
  if (base.includes(candidate) && candidate.length >= 6) {
    return true;
  }
  if (base.startsWith(candidate) && candidate.length >= Math.floor(base.length * 0.55)) {
    return true;
  }
  return false;
};

const truncateAtWordBoundary = (value, maxLength) => {
  const text = compactText(value, 500);
  const limit = Number(maxLength);
  if (!text || !Number.isFinite(limit) || text.length <= limit) return text;
  const candidate = text.slice(0, limit + 1);
  const boundary = candidate.lastIndexOf(' ');
  const slice = candidate
    .slice(0, boundary > Math.floor(limit * 0.55) ? boundary : limit)
    .replace(/[\s,.;:-]+$/g, '')
    .trim();
  return slice ? `${slice}...` : text.slice(0, limit).trim() + '...';
};

const normalizeSpotlightOffer = (value) => truncateAtWordBoundary(value, 168);

const getSpotlightFallbackLabel = (sponsorName) => {
  const words = String(sponsorName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const initials = words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
  return initials || 'Spotlight';
};

const SpotlightImage = ({ spotlight }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const fallbackLabel = getSpotlightFallbackLabel(spotlight?.sponsorName);
  const imageUrl = spotlight?.imageUrl || '';
  if (!imageUrl || imageFailed) {
    return (
      <div
        className="localops-region__spotlight-image localops-region__spotlight-image--fallback"
        aria-label={`${spotlight?.sponsorName || 'Local business'} Spotlight`}
        role="img"
      >
        {fallbackLabel}
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={`${spotlight.sponsorName} Spotlight`}
      className="localops-region__spotlight-image"
      loading="lazy"
      onError={() => setImageFailed(true)}
    />
  );
};


const normalizeSpotlight = (spotlight) => {
  if (!spotlight || typeof spotlight !== 'object') return null;
  const sponsorName = compactText(spotlight.sponsorName, 100);
  const rawHeadline = compactText(spotlight.headline || spotlight.title, 100);
  const offer = normalizeSpotlightOffer(spotlight.offer);
  if (!sponsorName || !offer) return null;
  const rawCategory = compactText(spotlight.businessCategory, 80);

  const destinationUrl = compactText(spotlight.destinationUrl, 500);
  const imageUrl = compactText(spotlight.imageUrl, 500);
  const trackingId = compactText(spotlight.trackingId, 128);
  return {
    trackingId,
    sponsorName,
    headline: isDuplicateSpotlightText(rawHeadline, sponsorName)
      ? ''
      : rawHeadline,
    offer,
    ctaLabel: compactText(spotlight.ctaLabel, 40) || 'Claim offer',
    destinationUrl: isSafeSpotlightUrl(destinationUrl) ? destinationUrl : '',
    imageUrl: isSafeSpotlightImageUrl(imageUrl) ? imageUrl : '',
    regionSlug: normalizeSlug(spotlight.regionSlug) || 'camden',
    businessCategory: isDuplicateSpotlightText(rawCategory, sponsorName)
      ? ''
      : rawCategory,
  };
};

const formatProofDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const prefersReducedMotion = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return (
    window.matchMedia('(prefers-reduced-motion: reduce)')?.matches === true
  );
};

const shouldOpenContactLinksDirectly = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const viewportWidth = Number(
    window.innerWidth || window.document?.documentElement?.clientWidth || 0,
  );
  if (viewportWidth > 0 && viewportWidth <= 767) return true;
  if (typeof window.matchMedia !== 'function') return false;
  try {
    return (
      Boolean(window.matchMedia('(max-width: 767px)')?.matches) ||
      Boolean(window.matchMedia('(hover: none) and (pointer: coarse)')?.matches)
    );
  } catch (err) {
    return false;
  }
};

const writeTextToClipboard = async (text) => {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    // Fall through to the legacy copy path.
  }

  try {
    if (typeof document === 'undefined' || !document.body) return false;
    const node = document.createElement('textarea');
    node.value = text;
    node.setAttribute('readonly', '');
    node.style.position = 'fixed';
    node.style.left = '-9999px';
    node.style.top = '0';
    document.body.appendChild(node);
    node.select();
    const copied =
      typeof document.execCommand === 'function' && document.execCommand('copy');
    document.body.removeChild(node);
    return Boolean(copied);
  } catch (err) {
    return false;
  }
};

const buildFirestarterReturnTo = ({ slug, src, intent = INTENTS.FIRESTARTER }) => {
  const params = new URLSearchParams();
  params.set('intent', intent);
  params.set('regionSlug', slug);
  params.set('source', 'offline_region');
  params.set('firestarter', '1');
  params.set('firestarterSurface', 'region_landing');
  params.set('firestarterCreate', 'task_errand');
  params.set('isCold', '1');
  if (src) params.set('src', src);
  return `/playermap?${params.toString()}`;
};

const buildPartnerAttributionParams = ({ slug, action }) => ({
  partnerIntent: INTENTS.PARTNER,
  partnerAction: action || PARTNER_ACTIONS.POST_TASK,
  marketSlug: slug,
  partnerSource: 'region_landing',
});

const ORGANIZATION_TYPE_OPTIONS = [
  'Business',
  'Restaurant / retail',
  'School',
  'Church',
  'Nonprofit',
  'City / civic group',
  'Event organizer',
  'Sponsor',
  'Other',
];

const HELP_CATEGORY_OPTIONS = [
  'Errands',
  'Deliveries / pickups',
  'Yard work / cleanup',
  'Event support',
  'Moving / lifting',
  'Admin / simple operations help',
  'Community projects',
  'Other',
];

const FREQUENCY_OPTIONS = [
  'One time',
  'Weekly',
  'Monthly',
  'Seasonal / event-based',
  'Not sure yet',
];

const TIMELINE_OPTIONS = [
  'Today / this week',
  'This month',
  'Planning ahead',
  'Not sure yet',
];

const BUDGET_RANGE_OPTIONS = [
  'Under $100',
  '$100-$500',
  '$500-$1,500',
  '$1,500+',
  'Not sure yet',
];

const buildInitialPartnerForm = () => ({
  organizationName: '',
  contactName: '',
  email: '',
  phone: '',
  organizationType: '',
  helpCategories: [],
  frequency: '',
  timeline: '',
  locationText: '',
  budgetRange: '',
  helpNeedSummary: '',
  notes: '',
  wantsRecurringHelp: false,
  wantsSponsorship: false,
  wantsFirstTask: false,
  website: '',
});

export default function LocalOpsRegionLanding() {
  const { slug: slugParam } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { player } = useContext(AuthContext);

  const queryParams = useMemo(
    () => new URLSearchParams(location.search || ''),
    [location.search],
  );
  const sourceTag = useMemo(
    () => getLocalOpsLaunchSourceTag(queryParams),
    [queryParams],
  );
  const slug = useMemo(
    () =>
      normalizeSlug(queryParams.get('slug') || slugParam || queryParams.get('region') || ''),
    [queryParams, slugParam],
  );
  const invalidHint = queryParams.get('invalid') === '1';

  const [status, setStatus] = useState('loading');
  const [regionData, setRegionData] = useState(null);
  const [selectedIntent, setSelectedIntent] = useState('');
  const [authExpanded, setAuthExpanded] = useState(false);
  const [authContext, setAuthContext] = useState(AUTH_CONTEXT.NONE);
  const [primaryBusy, setPrimaryBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [magicStatus, setMagicStatus] = useState('idle');
  const [magicMessage, setMagicMessage] = useState('');
  const [requesterHelpAuthHandoffId, setRequesterHelpAuthHandoffId] =
    useState('');
  const [exploreInterest, setExploreInterest] = useState('');
  const [exploreEmail, setExploreEmail] = useState('');
  const [exploreEmailError, setExploreEmailError] = useState('');
  const [exploreSubmitStatus, setExploreSubmitStatus] = useState('idle');
  const [exploreSubmitMessage, setExploreSubmitMessage] = useState('');
  const [exploreFormStartedAt, setExploreFormStartedAt] = useState(null);
  const [partnerAction, setPartnerAction] = useState('');
  const [partnerForm, setPartnerForm] = useState(buildInitialPartnerForm);
  const [partnerErrors, setPartnerErrors] = useState({});
  const [partnerSubmitStatus, setPartnerSubmitStatus] = useState('idle');
  const [partnerSubmitMessage, setPartnerSubmitMessage] = useState('');
  const [partnerFormStartedAt, setPartnerFormStartedAt] = useState(null);
  const [authScrollRequest, setAuthScrollRequest] = useState(0);
  const [contactFallbackOpen, setContactFallbackOpen] = useState(false);
  const [contactFallbackMode, setContactFallbackMode] = useState('');
  const [contactCopyStatus, setContactCopyStatus] = useState('');
  const [previewProof, setPreviewProof] = useState(null);
  const [spotlightIndex, setSpotlightIndex] = useState(0);
  const authChooserRef = useRef(null);
  const exploreSubmitPendingRef = useRef(false);
  const firestarterShownRef = useRef(false);
  const proofPreviewTriggerRef = useRef(null);
  const proofPreviewCloseRef = useRef(null);
  const spotlightImpressionsRef = useRef(new Set());

  useEffect(() => {
    if (!slug) {
      setStatus('invalid');
      setRegionData(null);
      return;
    }
    let cancelled = false;
    setStatus('loading');
    axiosConn
      .get(`offline-regions/resolve/${encodeURIComponent(slug)}`, {
        params: sourceTag ? { src: sourceTag } : undefined,
      })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data || null;
        if (!data || !data.region?.slug) {
          setStatus('invalid');
          setRegionData(null);
          return;
        }
        setRegionData(data);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        const statusCode = Number(err?.response?.status || 0);
        if (statusCode === 404 || invalidHint) {
          setStatus('invalid');
          setRegionData(null);
          return;
        }
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [slug, sourceTag, invalidHint]);

  const regionLabel = regionData?.region?.label || toRegionDisplayName(slug) || 'Local region';
  const regionSubtitle = regionData?.region?.subtitle || '';
  const examples = Array.isArray(regionData?.region?.examples)
    ? regionData.region.examples
    : [];
  const seedState = regionData?.seedState || null;
  const firestarterMode =
    typeof seedState?.mode === 'string' && seedState.mode.trim()
      ? seedState.mode.trim()
      : FIRESTARTER_MODE;
  const isColdRegion = seedState?.isCold === true;
  const isCollisionSpecialistRide = useMemo(
    () =>
      isCollisionSpecialistRideRequest({
        queryParams,
        regionSlug: slug,
      }),
    [queryParams, slug],
  );
  const launchRouteKind = isCollisionSpecialistRide
    ? 'collision_specialist_ride'
    : 'region_landing';
  const isCamdenRegion = slug === 'camden';
  const pageHeadline = isCollisionSpecialistRide
    ? CS_RIDE_HEADLINE
    : isCamdenRegion
    ? CAMDEN_PAGE_HEADLINE
    : regionSubtitle || regionLabel;
  const pageTrustLine = isCollisionSpecialistRide
    ? CS_RIDE_SUBCOPY
    : isCamdenRegion
    ? CAMDEN_PAGE_TRUST_LINE
    : 'JG LocalOps is launching here for local help, paid tasks, and verified contribution.';
  const pageHeroText = isCollisionSpecialistRide
    ? ''
    : isCamdenRegion
    ? CAMDEN_PAGE_SUBCOPY
    : regionData?.region?.hero ||
      'Request local help, find paid tasks when available, or learn how JG LocalOps works here.';
  const regionHelmet = useMemo(
    () =>
      buildRegionHelmet({
        isCollisionSpecialistRide,
        regionLabel,
        slug,
      }),
    [isCollisionSpecialistRide, regionLabel, slug],
  );
  const liveProofStats = useMemo(
    () => buildLiveProofStats(regionData),
    [regionData],
  );
  const proofReceipts = useMemo(
    () =>
      (Array.isArray(regionData?.featuredProofReceipts)
        ? regionData.featuredProofReceipts
        : [])
        .map(normalizeProofReceipt)
        .filter(Boolean)
        .slice(0, 3),
    [regionData],
  );
  const spotlightCards = useMemo(
    () =>
      (Array.isArray(regionData?.featuredSpotlights)
        ? regionData.featuredSpotlights
        : [])
        .map(normalizeSpotlight)
        .filter(Boolean),
    [regionData],
  );
  useEffect(() => {
    setSpotlightIndex(0);
  }, [spotlightCards.length]);
  useEffect(() => {
    if (spotlightCards.length <= 1 || prefersReducedMotion()) return undefined;
    const timer = window.setInterval(() => {
      setSpotlightIndex((current) => (current + 1) % spotlightCards.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [spotlightCards.length]);
  const visibleSpotlight =
    spotlightCards.length > 0
      ? spotlightCards[spotlightIndex % spotlightCards.length]
      : null;
  useEffect(() => {
    const trackingId = visibleSpotlight?.trackingId;
    if (!trackingId || spotlightImpressionsRef.current.has(trackingId)) {
      return;
    }
    spotlightImpressionsRef.current.add(trackingId);
    void trackSpotlightEvent({
      eventType: 'impression',
      trackingId,
      regionSlug: visibleSpotlight.regionSlug || slug,
      token: player?.token,
    });
  }, [player?.token, slug, visibleSpotlight]);

  const handleSpotlightClick = useCallback(
    (spotlight) => {
      if (!spotlight?.trackingId) return;
      void trackSpotlightEvent({
        eventType: 'click',
        trackingId: spotlight.trackingId,
        regionSlug: spotlight.regionSlug || slug,
        token: player?.token,
      });
    },
    [player?.token, slug],
  );

  const dominantPathIntent = useMemo(
    () =>
      isCamdenRegion
        ? resolveCamdenLandingPriority({
            intent: queryParams.get('intent') || '',
            src: sourceTag,
            isCollisionSpecialistRide,
          })
        : INTENTS.HELP,
    [isCamdenRegion, isCollisionSpecialistRide, queryParams, sourceTag],
  );
  const orderedPathCards = useMemo(() => {
    const cards = buildPathCards({ isCamdenRegion });
    return orderPathCards({
      cards,
      dominantIntent: dominantPathIntent,
      isCamdenRegion,
    });
  }, [dominantPathIntent, isCamdenRegion]);
  const primaryDominantIntent = useMemo(() => {
    if (!isCamdenRegion) return dominantPathIntent;
    if (dominantPathIntent === INTENTS.PARTNER) return INTENTS.PARTNER;
    if (dominantPathIntent === INTENTS.HELP) return INTENTS.HELP;
    return INTENTS.EARN;
  }, [dominantPathIntent, isCamdenRegion]);

  const selectedIntentIsAction =
    authContext === AUTH_CONTEXT.HELP ||
    authContext === AUTH_CONTEXT.EARN ||
    authContext === AUTH_CONTEXT.FIRESTARTER ||
    authContext === AUTH_CONTEXT.RIDE;
  const returnTo = useMemo(() => {
    if (!selectedIntentIsAction || !slug) return '/playermap';
    if (authContext === AUTH_CONTEXT.FIRESTARTER) {
      return buildFirestarterReturnTo({
        slug,
        src: sourceTag,
        intent:
          selectedIntent === INTENTS.HELP || selectedIntent === INTENTS.EARN
            ? selectedIntent
        : INTENTS.FIRESTARTER,
      });
    }
    if (authContext === AUTH_CONTEXT.RIDE) {
      const playerMapReturnTo = buildPlayerMapReturnTo({
        intent: COLLISION_SPECIALIST_RIDE_INTENT,
        slug,
        src: sourceTag || COLLISION_SPECIALIST_RIDE_SOURCE,
        isCold: false,
        extraParams: {
          marketSlug: slug,
          rideTemplate: COLLISION_SPECIALIST_RIDE_TEMPLATE,
        },
      });
      return buildLocalOpsContinueReturnTo(playerMapReturnTo);
    }
    const partnerExtraParams =
      selectedIntent === INTENTS.PARTNER && authContext === AUTH_CONTEXT.HELP
        ? buildPartnerAttributionParams({
            slug,
            action: partnerAction || PARTNER_ACTIONS.POST_TASK,
          })
        : {};
    const requesterStarterParams =
      selectedIntent === INTENTS.HELP && authContext === AUTH_CONTEXT.HELP
        ? buildRequesterStarterAutoOpenParams(requesterHelpAuthHandoffId)
        : {};
    const playerMapReturnTo = buildPlayerMapReturnTo({
      intent: authContext,
      slug,
      src: sourceTag,
      isCold: isColdRegion,
      extraParams: {
        ...partnerExtraParams,
        ...requesterStarterParams,
      },
    });
    return buildLocalOpsContinueReturnTo(playerMapReturnTo);
  }, [
    selectedIntentIsAction,
    authContext,
    selectedIntent,
    partnerAction,
    requesterHelpAuthHandoffId,
    slug,
    sourceTag,
    isColdRegion,
  ]);

  useEffect(() => {
    if (!authScrollRequest || !authExpanded || !selectedIntentIsAction) {
      return undefined;
    }
    const scrollAuthIntoView = () => {
      const node = authChooserRef.current;
      if (!node || typeof node.scrollIntoView !== 'function') return;
      node.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start',
      });
    };
    if (typeof window.requestAnimationFrame === 'function') {
      const frame = window.requestAnimationFrame(scrollAuthIntoView);
      return () => window.cancelAnimationFrame(frame);
    }
    const timer = window.setTimeout(scrollAuthIntoView, 0);
    return () => window.clearTimeout(timer);
  }, [authScrollRequest, authExpanded, selectedIntentIsAction]);

  const loginUrl = `/login?returnTo=${encodeURIComponent(returnTo)}`;
  const registerUrl = `/register?returnTo=${encodeURIComponent(
    returnTo,
  )}&source=offline_region&bonus=jgk_100`;
  const googleOAuthHref = isGoogleOAuthEnabled()
    ? getGoogleOAuthStartUrl({
        authBaseUrl: AUTH_BASE,
        returnTo,
      })
    : '';

  const emitLaunchVisitEvent = useCallback(
    (eventName, extra = {}) => {
      void trackLocalOpsLaunchEvent({
        slug,
        eventName,
        sourceTag,
        routeKind: launchRouteKind,
        intent:
          extra.intent ||
          selectedIntent ||
          queryParams.get('intent') ||
          '',
        ctaId: extra.ctaId,
        landingIntentType: extra.landingIntentType,
        targetKind: extra.targetKind,
        starterId: extra.starterId,
      });
    },
    [launchRouteKind, queryParams, selectedIntent, slug, sourceTag],
  );

  const closeProofPreview = useCallback(() => {
    setPreviewProof(null);
    window.setTimeout(() => {
      if (proofPreviewTriggerRef.current?.focus) {
        proofPreviewTriggerRef.current.focus();
      }
    }, 0);
  }, []);

  useEffect(() => {
    if (!previewProof) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeProofPreview();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeProofPreview, previewProof]);

  useEffect(() => {
    if (!previewProof) return;
    if (proofPreviewCloseRef.current?.focus) {
      proofPreviewCloseRef.current.focus();
    }
  }, [previewProof]);

  const emitFirestarterEvent = (eventName, extra = {}) => {
    const payload = {
      slug: slug || null,
      src: sourceTag || null,
      intent:
        selectedIntent === INTENTS.HELP || selectedIntent === INTENTS.EARN
          ? selectedIntent
          : null,
      isCold: isColdRegion === true,
      surface: 'region_landing',
      mode: firestarterMode,
      ...extra,
    };
    try {
      if (window?.dataLayer) {
        window.dataLayer.push({ event: eventName, ...payload });
      }
      if (window?.rudderanalytics?.track) {
        window.rudderanalytics.track(eventName, payload);
      }
      window.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
    } catch (err) {
      // ignore firestarter telemetry errors
    }
  };

  useEffect(() => {
    if (!isColdRegion || status !== 'ready') {
      firestarterShownRef.current = false;
      return;
    }
    if (firestarterShownRef.current) return;
    firestarterShownRef.current = true;
    emitFirestarterEvent('firestarter_shown');
  }, [isColdRegion, status, slug, sourceTag, firestarterMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const launchEventTrackedRef = useRef(new Set());
  const launchEventScheduledRef = useRef(new Set());

  useEffect(() => {
    if (status !== 'ready' || !slug) return undefined;
    const key = `${slug}|${sourceTag || 'direct'}|${launchRouteKind}`;
    const eventKey = `${key}|${LAUNCH_VISIT_EVENTS.LANDING_VIEWED}`;
    if (!launchEventTrackedRef.current.has(eventKey)) {
      launchEventTrackedRef.current.add(eventKey);
      emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.LANDING_VIEWED);
    }

    const timers = [];
    const scheduleEngagement = (eventName, delayMs) => {
      const engagementKey = `${key}|${eventName}`;
      if (
        launchEventTrackedRef.current.has(engagementKey) ||
        launchEventScheduledRef.current.has(engagementKey)
      ) {
        return;
      }
      launchEventScheduledRef.current.add(engagementKey);
      const timer = window.setTimeout(() => {
        launchEventScheduledRef.current.delete(engagementKey);
        if (launchEventTrackedRef.current.has(engagementKey)) return;
        launchEventTrackedRef.current.add(engagementKey);
        emitLaunchVisitEvent(eventName);
      }, delayMs);
      timers.push({ key: engagementKey, timer });
    };

    scheduleEngagement(LAUNCH_VISIT_EVENTS.ENGAGED_10S, 10000);
    scheduleEngagement(LAUNCH_VISIT_EVENTS.ENGAGED_60S, 60000);

    return () => {
      timers.forEach(({ key: timerKey, timer }) => {
        window.clearTimeout(timer);
        launchEventScheduledRef.current.delete(timerKey);
      });
    };
  }, [emitLaunchVisitEvent, launchRouteKind, slug, sourceTag, status]);

  const startHelpOrEarnIntent = (intent) => {
    setPartnerAction('');
    setPartnerErrors({});
    setPartnerSubmitStatus('idle');
    const nextContext = intent === INTENTS.HELP ? AUTH_CONTEXT.HELP : AUTH_CONTEXT.EARN;
    setRequesterHelpAuthHandoffId(
      intent === INTENTS.HELP && !player?.token
        ? createRequesterStarterAutoOpenId()
        : '',
    );
    setAuthContext(nextContext);
    if (player?.token) {
      history.push(
        buildPlayerMapReturnTo({
          intent,
          slug,
          src: sourceTag,
          isCold: isColdRegion,
        }),
      );
      return;
    }
    setAuthExpanded(true);
    setAuthScrollRequest((count) => count + 1);
  };

  const handleIntentChoice = (intent) => {
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: `intent_${intent}`,
      intent,
    });
    setSelectedIntent(intent);
    setMagicMessage('');
    setPartnerSubmitMessage('');
    if (intent === INTENTS.EXPLORE) {
      setRequesterHelpAuthHandoffId('');
      setAuthExpanded(false);
      setAuthContext(AUTH_CONTEXT.NONE);
      setPartnerAction('');
      setExploreSubmitStatus('idle');
      setExploreSubmitMessage('');
      setExploreEmailError('');
      setExploreFormStartedAt(Date.now());
      return;
    }
    if (intent === INTENTS.PARTNER) {
      setRequesterHelpAuthHandoffId('');
      setAuthExpanded(false);
      setAuthContext(AUTH_CONTEXT.NONE);
      setPartnerAction('');
      setPartnerErrors({});
      setPartnerSubmitStatus('idle');
      return;
    }
    startHelpOrEarnIntent(intent);
  };

  const handleProofPreviewClick = (proof, event) => {
    proofPreviewTriggerRef.current = event?.currentTarget || null;
    setPreviewProof(proof);
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: 'landing_featured_apr_preview_clicked',
      targetKind: 'completion_proof_share',
    });
  };

  const handleProofFullReceiptClick = () => {
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: 'landing_featured_apr_full_receipt_clicked',
      targetKind: 'completion_proof_share',
    });
  };

  const handleProofPostLikeThisClick = () => {
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: 'landing_featured_apr_post_like_this_clicked',
      intent: INTENTS.HELP,
      targetKind: 'completion_proof_share',
    });
    setPreviewProof(null);
    setSelectedIntent(INTENTS.HELP);
    setMagicMessage('');
    setPartnerSubmitMessage('');
    startHelpOrEarnIntent(INTENTS.HELP);
  };

  const handleProofEarnClick = () => {
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: 'landing_featured_apr_earn_clicked',
      intent: INTENTS.EARN,
      targetKind: 'completion_proof_share',
    });
    setPreviewProof(null);
    setSelectedIntent(INTENTS.EARN);
    setMagicMessage('');
    setPartnerSubmitMessage('');
    startHelpOrEarnIntent(INTENTS.EARN);
  };

  const handleProofPreviewBackdropMouseDown = (event) => {
    if (event.target === event.currentTarget) {
      closeProofPreview();
    }
  };

  const handleFirestarterCta = () => {
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: 'firestarter_create_first_task',
      intent: INTENTS.FIRESTARTER,
    });
    setSelectedIntent(INTENTS.FIRESTARTER);
    setAuthContext(AUTH_CONTEXT.FIRESTARTER);
    setRequesterHelpAuthHandoffId('');
    setMagicMessage('');
    emitFirestarterEvent('firestarter_clicked', { intent: INTENTS.FIRESTARTER });
    if (player?.token) {
      history.push(
        buildFirestarterReturnTo({
          slug,
          src: sourceTag,
        }),
      );
      return;
    }
    setAuthExpanded(true);
    setAuthScrollRequest((count) => count + 1);
    emitFirestarterEvent('firestarter_auth_chooser_shown', {
      intent: INTENTS.FIRESTARTER,
    });
  };

  const handleCollisionSpecialistRideCta = () => {
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: 'collision_specialist_ride',
      intent: COLLISION_SPECIALIST_RIDE_INTENT,
    });
    setSelectedIntent(COLLISION_SPECIALIST_RIDE_INTENT);
    setAuthContext(AUTH_CONTEXT.RIDE);
    setRequesterHelpAuthHandoffId('');
    setMagicMessage('');
    setPartnerAction('');
    setPartnerErrors({});
    setPartnerSubmitMessage('');
    setContactFallbackOpen(false);
    setContactCopyStatus('');
    const playerMapReturnTo = buildPlayerMapReturnTo({
      intent: COLLISION_SPECIALIST_RIDE_INTENT,
      slug,
      src: sourceTag || COLLISION_SPECIALIST_RIDE_SOURCE,
      isCold: false,
      extraParams: {
        marketSlug: slug,
        rideTemplate: COLLISION_SPECIALIST_RIDE_TEMPLATE,
      },
    });
    if (player?.token) {
      history.push(playerMapReturnTo);
      return;
    }
    setAuthExpanded(true);
    setAuthScrollRequest((count) => count + 1);
  };

  const handleCollisionSpecialistFallback = (ctaId) => {
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId,
      intent: COLLISION_SPECIALIST_RIDE_INTENT,
    });
  };

  const handleCollisionSpecialistContactLink = (event, mode) => {
    const ctaId =
      mode === 'sms'
        ? 'collision_specialist_ride_sms_fallback'
        : 'collision_specialist_ride_call_fallback';
    handleCollisionSpecialistFallback(ctaId);
    if (shouldOpenContactLinksDirectly()) return;
    event.preventDefault();
    setContactFallbackMode(mode);
    setContactCopyStatus('');
    setContactFallbackOpen(true);
  };

  const handleCollisionSpecialistCopy = async (copyType) => {
    const isPhone = copyType === 'phone';
    handleCollisionSpecialistFallback(
      isPhone
        ? 'collision_specialist_ride_copy_phone'
        : 'collision_specialist_ride_copy_sms',
    );
    const copied = await writeTextToClipboard(
      isPhone ? CS_RIDE_HELP_PHONE_LABEL : CS_RIDE_SMS_BODY,
    );
    setContactCopyStatus(
      copied
        ? isPhone
          ? 'phone'
          : 'sms'
        : 'unavailable',
    );
  };

  const handleExploreCamdenFromRide = () => {
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: 'collision_specialist_ride_explore_camden',
      intent: INTENTS.EXPLORE,
    });
    const params = new URLSearchParams();
    if (sourceTag) params.set('src', sourceTag);
    setSelectedIntent('');
    setAuthExpanded(false);
    setAuthContext(AUTH_CONTEXT.NONE);
    setRequesterHelpAuthHandoffId('');
    setMagicMessage('');
    setPartnerAction('');
    setContactFallbackOpen(false);
    setContactCopyStatus('');
    history.push(`/localops/region/${encodeURIComponent(slug)}${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handlePartnerPostTask = () => {
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: 'partner_post_task',
      intent: INTENTS.PARTNER,
    });
    setSelectedIntent(INTENTS.PARTNER);
    setPartnerAction(PARTNER_ACTIONS.POST_TASK);
    setAuthContext(AUTH_CONTEXT.HELP);
    setRequesterHelpAuthHandoffId('');
    setMagicMessage('');
    setPartnerErrors({});
    setPartnerSubmitMessage('');
    if (player?.token) {
      history.push(
        buildPlayerMapReturnTo({
          intent: INTENTS.HELP,
          slug,
          src: sourceTag,
          isCold: isColdRegion,
          extraParams: buildPartnerAttributionParams({
            slug,
            action: PARTNER_ACTIONS.POST_TASK,
          }),
        }),
      );
      return;
    }
    setAuthExpanded(true);
    setAuthScrollRequest((count) => count + 1);
  };

  const openPartnerLeadForm = (action) => {
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: action,
      intent: INTENTS.PARTNER,
    });
    setSelectedIntent(INTENTS.PARTNER);
    setAuthExpanded(false);
    setAuthContext(AUTH_CONTEXT.NONE);
    setRequesterHelpAuthHandoffId('');
    setPartnerAction(action);
    setPartnerErrors({});
    setPartnerSubmitStatus('idle');
    setPartnerSubmitMessage('');
    setPartnerFormStartedAt(Date.now());
    setPartnerForm({
      ...buildInitialPartnerForm(),
      organizationType:
        action === PARTNER_ACTIONS.SPONSOR_OPPORTUNITIES ? 'Sponsor' : '',
      wantsRecurringHelp: action === PARTNER_ACTIONS.RECURRING_HELP,
      wantsSponsorship: action === PARTNER_ACTIONS.SPONSOR_OPPORTUNITIES,
      wantsFirstTask: false,
    });
  };

  const handlePrimaryAuth = () => {
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: 'auth_register',
      intent: authContext || selectedIntent,
    });
    history.push(registerUrl);
  };

  const validateEmail = (value) => {
    if (!value) return 'Email is required.';
    const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!re.test(value.trim())) return 'Enter a valid email address.';
    return '';
  };

  const validatePartnerLeadForm = () => {
    const errors = {};
    const emailValidation = validateEmail(partnerForm.email);
    const phoneDigits = String(partnerForm.phone || '').replace(/\D/g, '');
    if (!partnerForm.organizationName.trim()) {
      errors.organizationName = 'Organization name is required.';
    }
    if (!partnerForm.contactName.trim()) {
      errors.contactName = 'Contact name is required.';
    }
    if (emailValidation) {
      errors.email = emailValidation;
    }
    if (!partnerForm.phone.trim()) {
      errors.phone = 'Phone is required.';
    } else if (phoneDigits.length < 10) {
      errors.phone = 'Enter a valid phone number.';
    }
    if (!partnerForm.organizationType) {
      errors.organizationType = 'Organization type is required.';
    }
    if (!partnerForm.helpNeedSummary.trim()) {
      errors.helpNeedSummary = 'Tell us what kind of local help you need.';
    }
    return errors;
  };

  const updatePartnerField = (field, value) => {
    setPartnerForm((current) => ({
      ...current,
      [field]: value,
    }));
    if (partnerErrors[field]) {
      setPartnerErrors((current) => {
        const next = { ...current };
        delete next[field];
        return next;
      });
    }
  };

  const togglePartnerCategory = (category) => {
    setPartnerForm((current) => {
      const currentCategories = Array.isArray(current.helpCategories)
        ? current.helpCategories
        : [];
      const hasCategory = currentCategories.includes(category);
      return {
        ...current,
        helpCategories: hasCategory
          ? currentCategories.filter((item) => item !== category)
          : [...currentCategories, category],
      };
    });
    if (partnerErrors.helpCategories) {
      setPartnerErrors((current) => {
        const next = { ...current };
        delete next.helpCategories;
        return next;
      });
    }
  };

  const handlePartnerLeadSubmit = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: 'partner_lead_submit',
      intent: INTENTS.PARTNER,
    });
    const errors = validatePartnerLeadForm();
    if (Object.keys(errors).length > 0) {
      setPartnerErrors(errors);
      setPartnerSubmitStatus('blocked');
      setPartnerSubmitMessage('Email, phone, and the required fields are needed to submit.');
      return;
    }
    setPartnerErrors({});
    setPartnerSubmitStatus('submitting');
    setPartnerSubmitMessage('');
    try {
      await axiosConn.post(`offline-regions/${encodeURIComponent(slug)}/partner-leads`, {
        ...partnerForm,
        marketSlug: slug,
        source: 'region_landing',
        src: sourceTag || '',
        intent: INTENTS.PARTNER,
        partnerAction: partnerAction || PARTNER_ACTIONS.RECURRING_HELP,
        formStartedAt: partnerFormStartedAt || Date.now(),
      });
      setPartnerSubmitStatus('success');
      setPartnerSubmitMessage('Partner lead submitted.');
    } catch (err) {
      const statusCode = Number(err?.response?.status || 0);
      const apiErrors = err?.response?.data?.errors || null;
      setPartnerSubmitStatus('error');
      if (apiErrors && typeof apiErrors === 'object') {
        setPartnerErrors(apiErrors);
      }
      setPartnerSubmitMessage(
        statusCode === 429
          ? 'Too many attempts. Please try again later.'
          : 'Could not submit this lead right now. Please try again.',
      );
    }
  };

  const handleMagicLink = async (payload = {}) => {
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: 'auth_magic_link',
      intent: authContext || selectedIntent,
    });
    const validation = validateEmail(email);
    setEmailError(validation);
    if (validation) return;
    setMagicStatus('sending');
    setMagicMessage('');
    try {
      const magicUrl = AUTH_BASE ? `${AUTH_BASE}/auth/magic` : '/auth/magic';
      const res = await fetch(magicUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          email,
          returnTo,
          website: payload.website || '',
        }),
      });
      if (!res.ok) throw new Error('magic-link-failed');
      setMagicStatus('sent');
      setMagicMessage('');
    } catch (err) {
      setMagicStatus('error');
      setMagicMessage('Could not send magic link. Please try again.');
    }
  };

  const handleExploreSubmit = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (exploreSubmitPendingRef.current) return;
    emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
      ctaId: 'explore_launch_update_submit',
      intent: INTENTS.EXPLORE,
    });

    const validation = validateEmail(exploreEmail);
    if (validation) {
      setExploreEmailError(validation);
      setExploreSubmitStatus('blocked');
      setExploreSubmitMessage('Email is required to request Camden launch updates.');
      return;
    }

    const trimmedEmail = exploreEmail.trim();
    const trimmedInterest = exploreInterest.trim();
    const payload = {
      email: trimmedEmail,
      src: sourceTag || '',
      formStartedAt: exploreFormStartedAt || Date.now(),
      website: '',
      consentTextVersion: LAUNCH_UPDATE_CONSENT_TEXT_VERSION,
    };
    if (trimmedInterest) {
      payload.interest = trimmedInterest;
    }

    exploreSubmitPendingRef.current = true;
    setExploreEmailError('');
    setExploreSubmitStatus('submitting');
    setExploreSubmitMessage('');
    try {
      await axiosConn.post(
        `offline-regions/${encodeURIComponent(slug)}/launch-updates`,
        payload,
      );
      setExploreEmail(trimmedEmail);
      setExploreSubmitStatus('success');
      setExploreSubmitMessage(LAUNCH_UPDATE_SUCCESS_COPY);
    } catch (err) {
      const statusCode = Number(err?.response?.status || 0);
      setExploreSubmitStatus('error');
      setExploreSubmitMessage(
        statusCode === 429
          ? 'Too many attempts. Please try again later.'
          : 'Could not request Camden launch updates right now. Please try again.',
      );
    } finally {
      exploreSubmitPendingRef.current = false;
    }
  };

  const partnerLeadActionLabel =
    partnerAction === PARTNER_ACTIONS.SPONSOR_OPPORTUNITIES
      ? 'Sponsor local opportunities'
      : 'Set up recurring local help';
  const partnerLeadSuccessTitle =
    partnerAction === PARTNER_ACTIONS.SPONSOR_OPPORTUNITIES
      ? "You're on the Camden partner list."
      : "You're on the Camden launch list.";
  const partnerLeadSuccessCopy =
    partnerAction === PARTNER_ACTIONS.SPONSOR_OPPORTUNITIES
      ? "We'll follow up about setting up a Camden opportunity pool. Want to move now? Create your first task while we review your request."
      : "We'll review your local work need and follow up with the fastest way to get started. Want to move now? Create your first task while we review your request.";

  const renderPartnerFieldError = (field) =>
    partnerErrors[field] ? (
      <div className="localops-region__field-error">{partnerErrors[field]}</div>
    ) : null;

  const renderPartnerLeadForm = () => {
    if (
      partnerAction !== PARTNER_ACTIONS.RECURRING_HELP &&
      partnerAction !== PARTNER_ACTIONS.SPONSOR_OPPORTUNITIES
    ) {
      return null;
    }

    if (partnerSubmitStatus === 'success') {
      return (
        <div
          className="localops-region__partner-success"
          data-testid="offline-region-partner-success"
        >
          <h3>{partnerLeadSuccessTitle}</h3>
          <p>{partnerLeadSuccessCopy}</p>
          <div className="localops-region__partner-success-actions">
            <button
              type="button"
              className="localops-region__intent localops-region__intent--partner"
              onClick={handlePartnerPostTask}
            >
              Create first task
            </button>
            <button
              type="button"
              className="localops-region__secondary-action"
              onClick={() => handleIntentChoice(INTENTS.EXPLORE)}
            >
              Explore JG LocalOps in Camden
            </button>
          </div>
        </div>
      );
    }

    return (
      <form
        className="localops-region__partner-form"
        data-testid="offline-region-partner-lead-form"
        onSubmit={handlePartnerLeadSubmit}
      >
        <div className="localops-region__partner-form-heading">
          <div className="localops-region__section-eyebrow">
            {partnerAction === PARTNER_ACTIONS.SPONSOR_OPPORTUNITIES
              ? 'Sponsor / institution lead'
              : 'Business / organization lead'}
          </div>
          <h3>{partnerLeadActionLabel}</h3>
        </div>

        <label htmlFor="partner-organization-name" className="localops-region__field-label">
          Organization name
        </label>
        <input
          id="partner-organization-name"
          className="localops-region__input"
          value={partnerForm.organizationName}
          onChange={(e) => updatePartnerField('organizationName', e.target.value)}
          maxLength={120}
        />
        {renderPartnerFieldError('organizationName')}

        <label htmlFor="partner-contact-name" className="localops-region__field-label">
          Contact name
        </label>
        <input
          id="partner-contact-name"
          className="localops-region__input"
          value={partnerForm.contactName}
          onChange={(e) => updatePartnerField('contactName', e.target.value)}
          maxLength={100}
        />
        {renderPartnerFieldError('contactName')}

        <div className="localops-region__field-grid">
          <div>
            <label htmlFor="partner-email" className="localops-region__field-label">
              Email
            </label>
            <input
              id="partner-email"
              className="localops-region__input"
              type="email"
              value={partnerForm.email}
              onChange={(e) => updatePartnerField('email', e.target.value)}
              maxLength={160}
            />
            {renderPartnerFieldError('email')}
          </div>
          <div>
            <label htmlFor="partner-phone" className="localops-region__field-label">
              Phone
            </label>
            <input
              id="partner-phone"
              className="localops-region__input"
              type="tel"
              value={partnerForm.phone}
              onChange={(e) => updatePartnerField('phone', e.target.value)}
              maxLength={24}
            />
            {renderPartnerFieldError('phone')}
          </div>
        </div>

        <label htmlFor="partner-organization-type" className="localops-region__field-label">
          Organization type
        </label>
        <select
          id="partner-organization-type"
          className="localops-region__input"
          value={partnerForm.organizationType}
          onChange={(e) => updatePartnerField('organizationType', e.target.value)}
        >
          <option value="">Choose one</option>
          {ORGANIZATION_TYPE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        {renderPartnerFieldError('organizationType')}

        <fieldset className="localops-region__checkbox-group">
          <legend>Help categories</legend>
          <div className="localops-region__checkbox-grid">
            {HELP_CATEGORY_OPTIONS.map((category) => (
              <label key={category} className="localops-region__checkbox-label">
                <input
                  type="checkbox"
                  checked={partnerForm.helpCategories.includes(category)}
                  onChange={() => togglePartnerCategory(category)}
                />
                <span>{category}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="localops-region__field-grid">
          <div>
            <label htmlFor="partner-frequency" className="localops-region__field-label">
              Frequency
            </label>
            <select
              id="partner-frequency"
              className="localops-region__input"
              value={partnerForm.frequency}
              onChange={(e) => updatePartnerField('frequency', e.target.value)}
            >
              <option value="">Optional</option>
              {FREQUENCY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="partner-timeline" className="localops-region__field-label">
              Timeline
            </label>
            <select
              id="partner-timeline"
              className="localops-region__input"
              value={partnerForm.timeline}
              onChange={(e) => updatePartnerField('timeline', e.target.value)}
            >
              <option value="">Optional</option>
              {TIMELINE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="localops-region__field-grid">
          <div>
            <label htmlFor="partner-location-text" className="localops-region__field-label">
              Location
            </label>
            <input
              id="partner-location-text"
              className="localops-region__input"
              value={partnerForm.locationText}
              onChange={(e) => updatePartnerField('locationText', e.target.value)}
              maxLength={180}
              placeholder="Camden area, event site, or route"
            />
          </div>
          <div>
            <label htmlFor="partner-budget-range" className="localops-region__field-label">
              Budget range
            </label>
            <select
              id="partner-budget-range"
              className="localops-region__input"
              value={partnerForm.budgetRange}
              onChange={(e) => updatePartnerField('budgetRange', e.target.value)}
            >
              <option value="">Optional</option>
              {BUDGET_RANGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label htmlFor="partner-help-summary" className="localops-region__field-label">
          What local help do you need?
        </label>
        <textarea
          id="partner-help-summary"
          className="localops-region__textarea"
          value={partnerForm.helpNeedSummary}
          onChange={(e) => updatePartnerField('helpNeedSummary', e.target.value)}
          maxLength={1200}
        />
        {renderPartnerFieldError('helpNeedSummary')}

        <label htmlFor="partner-notes" className="localops-region__field-label">
          Notes
        </label>
        <textarea
          id="partner-notes"
          className="localops-region__textarea localops-region__textarea--compact"
          value={partnerForm.notes}
          onChange={(e) => updatePartnerField('notes', e.target.value)}
          maxLength={1200}
        />

        <label className="localops-region__honeypot" aria-hidden="true">
          Website
          <input
            tabIndex="-1"
            autoComplete="off"
            value={partnerForm.website}
            onChange={(e) => updatePartnerField('website', e.target.value)}
          />
        </label>

        <button
          type="submit"
          className="localops-region__intent localops-region__intent--partner"
          data-testid="offline-region-partner-submit"
          disabled={partnerSubmitStatus === 'submitting'}
        >
          {partnerSubmitStatus === 'submitting' ? 'Submitting...' : 'Submit'}
        </button>
        {partnerSubmitMessage ? (
          <div
            className="localops-region__note"
            data-testid="offline-region-partner-submit-status"
            data-status={partnerSubmitStatus}
          >
            {partnerSubmitMessage}
          </div>
        ) : null}
      </form>
    );
  };

  if (status === 'loading') {
    return (
      <>
        <GenericHelmet {...regionHelmet} />
        <div className="localops-region">
          <div className="localops-region__card">
            <h2>Loading region</h2>
            <p>Preparing your LocalOps entry point...</p>
          </div>
        </div>
      </>
    );
  }

  if (status === 'invalid') {
    return (
      <>
        <GenericHelmet {...regionHelmet} />
        <div className="localops-region">
          <div className="localops-region__card localops-region__card--invalid">
            <h2>Invalid region link</h2>
            <p>This offline region link is unavailable. Please check the sign and try again.</p>
          </div>
        </div>
      </>
    );
  }

  if (status === 'error') {
    return (
      <>
        <GenericHelmet {...regionHelmet} />
        <div className="localops-region">
          <div className="localops-region__card localops-region__card--invalid">
            <h2>Region unavailable</h2>
            <p>We couldn't load this region right now. Please try again shortly.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <GenericHelmet {...regionHelmet} />
      <div className="localops-region" data-testid="offline-region-sorter">
      <div className="localops-region__card">
        <div className="localops-region__badge">
          <JGLocalOpsMark label="JG LocalOps Camden launch" />
        </div>
        <h1 className="localops-region__title">{pageHeadline}</h1>
        <p className="localops-region__value-line">
          {pageTrustLine}
        </p>
        {pageHeroText ? <p className="localops-region__hero">{pageHeroText}</p> : null}
        {!isCollisionSpecialistRide ? (
          <section
            className="localops-region__live-proof"
            data-testid="localops-live-proof-strip"
            aria-label="JG LocalOps Live"
          >
            <div className="localops-region__live-proof-label">
              JG LocalOps Live
            </div>
            {liveProofStats.length > 0 ? (
              <div className="localops-region__live-proof-stats">
                {liveProofStats.map((stat) => (
                  <span
                    key={stat.key}
                    className="localops-region__live-proof-stat"
                  >
                    <strong>{formatCount(stat.value)}</strong>
                    <span>{stat.label}</span>
                  </span>
                ))}
              </div>
            ) : (
              <p>{LIVE_PROOF_FALLBACK_COPY}</p>
            )}
          </section>
        ) : null}
        {!isCollisionSpecialistRide ? (
          <div className="localops-region__trust-row" aria-label="public flow trust indicators">
            <span className="localops-region__trust-pill">
              {isCamdenRegion ? CAMDEN_PAGE_SECONDARY_TRUST : 'Public preview'}
            </span>
            <span className="localops-region__trust-pill">Browse before signing in</span>
            <span className="localops-region__trust-pill">
              Choose a path, then continue on the local map
            </span>
          </div>
        ) : null}
        {isColdRegion && !isCollisionSpecialistRide ? (
          <div className="localops-region__firestarter" data-testid="firestarter-region-state">
            <div className="localops-region__section-eyebrow">Firestarter mode</div>
            <h3 className="localops-region__firestarter-title">
              Post the first local task in {regionLabel}
            </h3>
            <p className="localops-region__firestarter-copy">
              Start with a small task: a pickup, errand, cleaning help, home
              help, or food run. For paid tasks, you approve completion only
              when satisfied.
            </p>
            <button
              type="button"
              className="localops-region__intent localops-region__intent--help"
              data-testid="firestarter-region-cta"
              onClick={handleFirestarterCta}
            >
              Create the first task here
            </button>
          </div>
        ) : null}

        {isCollisionSpecialistRide ? (
          <div
            className="localops-region__ride-card"
            data-testid="collision-specialist-ride-context"
          >
            <div className="localops-region__section-eyebrow">
              Collision Specialist pickup flow
            </div>
            <h3 className="localops-region__ride-title">
              Ride request from Collision Specialist
            </h3>
            <p className="localops-region__ride-copy">
              Pickup is already set. Use email to continue, then add where
              you are going so a local helper can accept.
            </p>
            <div className="localops-region__ride-pickup">
              <span>Pickup</span>
              <strong>{COLLISION_SPECIALIST_PICKUP_ADDRESS}</strong>
            </div>
            <ul className="localops-region__ride-proof">
              {CS_RIDE_TRUST_BULLETS.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
            <div className="localops-region__ride-actions">
              <button
                type="button"
                className="localops-region__intent localops-region__intent--help"
                data-testid="collision-specialist-ride-cta"
                onClick={handleCollisionSpecialistRideCta}
              >
                Start my ride request
              </button>
              <div className="localops-region__ride-fallback-actions">
                <a
                  className="localops-region__secondary-action"
                  data-testid="collision-specialist-ride-text-fallback"
                  href={buildSmsHref(CS_RIDE_HELP_PHONE, CS_RIDE_SMS_BODY)}
                  onClick={(event) =>
                    handleCollisionSpecialistContactLink(event, 'sms')
                  }
                >
                  Text for help
                </a>
                <a
                  className="localops-region__secondary-action"
                  data-testid="collision-specialist-ride-call-fallback"
                  href={`tel:${CS_RIDE_HELP_PHONE}`}
                  onClick={(event) =>
                    handleCollisionSpecialistContactLink(event, 'call')
                  }
                >
                  Call for help
                </a>
              </div>
              {contactFallbackOpen ? (
                <div
                  className="localops-region__contact-fallback"
                  data-testid="collision-specialist-contact-fallback"
                  role="dialog"
                  aria-label="Text or call JG LocalOps"
                >
                  <div className="localops-region__contact-fallback-header">
                    <div>
                      <div className="localops-region__section-eyebrow">
                        {contactFallbackMode === 'call' ? 'Call fallback' : 'Text fallback'}
                      </div>
                      <h4>Text or call JG LocalOps</h4>
                    </div>
                    <button
                      type="button"
                      className="localops-region__contact-close"
                      aria-label="Close contact fallback"
                      onClick={() => setContactFallbackOpen(false)}
                    >
                      x
                    </button>
                  </div>
                  <p
                    className="localops-region__contact-phone"
                    data-testid="collision-specialist-contact-phone"
                  >
                    {CS_RIDE_HELP_PHONE_LABEL}
                  </p>
                  <div className="localops-region__contact-message">
                    <span>Suggested text:</span>
                    <pre data-testid="collision-specialist-contact-sms-body">
                      {CS_RIDE_SMS_BODY}
                    </pre>
                  </div>
                  <div className="localops-region__contact-actions">
                    <button
                      type="button"
                      className="localops-region__secondary-action"
                      data-testid="collision-specialist-copy-phone"
                      onClick={() => handleCollisionSpecialistCopy('phone')}
                    >
                      Copy phone number
                    </button>
                    <button
                      type="button"
                      className="localops-region__secondary-action"
                      data-testid="collision-specialist-copy-sms"
                      onClick={() => handleCollisionSpecialistCopy('sms')}
                    >
                      Copy text message
                    </button>
                    <a
                      className="localops-region__secondary-action"
                      data-testid="collision-specialist-open-sms-app"
                      href={buildSmsHref(CS_RIDE_HELP_PHONE, CS_RIDE_SMS_BODY)}
                      onClick={() =>
                        handleCollisionSpecialistFallback(
                          'collision_specialist_ride_sms_fallback',
                        )
                      }
                    >
                      Open SMS app
                    </a>
                    <a
                      className="localops-region__secondary-action"
                      data-testid="collision-specialist-open-call-app"
                      href={`tel:${CS_RIDE_HELP_PHONE}`}
                      onClick={() =>
                        handleCollisionSpecialistFallback(
                          'collision_specialist_ride_call_fallback',
                        )
                      }
                    >
                      Open call app
                    </a>
                  </div>
                  <div className="localops-region__contact-copy-status" aria-live="polite">
                    {contactCopyStatus === 'phone' ? 'Phone number copied.' : null}
                    {contactCopyStatus === 'sms' ? 'Text message copied.' : null}
                    {contactCopyStatus === 'unavailable'
                      ? 'Copy unavailable. Select the text above to copy it.'
                      : null}
                  </div>
                </div>
              ) : null}
              <p className="localops-region__ride-fallback">
                Help line: {CS_RIDE_HELP_PHONE_LABEL}
              </p>
              <button
                type="button"
                className="localops-region__login-link localops-region__ride-explore"
                data-testid="collision-specialist-ride-explore-camden"
                onClick={handleExploreCamdenFromRide}
              >
                Not here for a ride? Explore JG LocalOps Camden.
              </button>
            </div>
          </div>
        ) : null}

        {!isCollisionSpecialistRide ? (
          <div className="localops-region__intents" data-testid="offline-region-intent-grid">
            {orderedPathCards.map((card) => {
              const dominant =
                isCamdenRegion && card.intent === primaryDominantIntent;
              return (
                <button
                  key={card.intent}
                  type="button"
                  className={[
                    'localops-region__intent',
                    card.className,
                    dominant ? 'localops-region__intent--dominant' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  data-dominant={dominant ? 'true' : 'false'}
                  onClick={() => handleIntentChoice(card.intent)}
                >
                  {dominant && card.badge ? (
                    <span className="localops-region__intent-badge">
                      {card.badge}
                    </span>
                  ) : null}
                  <span className="localops-region__intent-title">
                    {card.title}
                  </span>
                  <small className="localops-region__intent-copy">
                    {card.copy}
                  </small>
                  {card.ctaLabel ? (
                    <span className="localops-region__intent-cta">
                      {card.ctaLabel}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : null}

        {isCamdenRegion && !isCollisionSpecialistRide ? (
          <div
            className="localops-region__path-links"
            aria-label="More Camden LocalOps paths"
          >
            {dominantPathIntent !== INTENTS.PARTNER ? (
              <button
                type="button"
                className="localops-region__secondary-action localops-region__path-link"
                onClick={() => handleIntentChoice(INTENTS.PARTNER)}
              >
                Run a Camden business? Work with LocalOps
              </button>
            ) : null}
            <button
              type="button"
              className="localops-region__secondary-action localops-region__path-link"
              onClick={() => handleIntentChoice(INTENTS.EXPLORE)}
            >
              Browse Camden activity
            </button>
          </div>
        ) : null}

        {authExpanded && selectedIntentIsAction ? (
          <div
            ref={authChooserRef}
            className="localops-region__auth"
            data-testid="offline-region-auth-chooser"
          >
            <div className="localops-region__section-eyebrow">Sign in to continue</div>
            <AuthChooser
              primaryAuthMethod="magic_link"
              headerText={
                authContext === AUTH_CONTEXT.FIRESTARTER
                  ? `Create the first task in ${regionLabel}`
                  : authContext === AUTH_CONTEXT.RIDE
                  ? 'Need a ride from Collision Specialist?'
                  : selectedIntent === INTENTS.PARTNER
                  ? `Post a task in ${regionLabel}`
                  : authContext === AUTH_CONTEXT.HELP
                  ? `Need help in ${regionLabel}?`
                  : `Want to earn in ${regionLabel}?`
              }
              subText={
                authContext === AUTH_CONTEXT.FIRESTARTER
                  ? 'Sign in quickly, then publish the first LocalOps task here.'
                : authContext === AUTH_CONTEXT.RIDE
                  ? 'Use email to continue to your filled ride request.'
                : selectedIntent === INTENTS.PARTNER
                  ? `Use email to continue to the ${regionLabel} LocalOps map.`
                  : "Use email to continue this LocalOps action. We'll bring you back here."
              }
              onPrimaryStart={() => setPrimaryBusy(true)}
              onPrimary={handlePrimaryAuth}
              primaryDisabled={primaryBusy}
              email={email}
              onEmailChange={setEmail}
              emailError={emailError}
              onEmailErrorClear={() => setEmailError('')}
              onSendMagicLink={handleMagicLink}
              magicStatus={magicStatus}
              googleOAuthEnabled={!!googleOAuthHref}
              googleOAuthHref={googleOAuthHref}
            />
            {magicMessage ? (
              <div
                className="localops-region__magic-status localops-region__magic-status--error"
                role="alert"
              >
                {magicMessage}
              </div>
            ) : null}
            <button
              type="button"
              className="localops-region__login-link"
              onClick={() => {
                emitLaunchVisitEvent(LAUNCH_VISIT_EVENTS.CTA_CLICKED, {
                  ctaId: 'auth_login',
                  intent: authContext || selectedIntent,
                });
                history.push(loginUrl);
              }}
            >
              Already have an account? Sign in
            </button>
          </div>
        ) : null}

        {selectedIntent === INTENTS.PARTNER ? (
          <div className="localops-region__partner" data-testid="offline-region-partner-surface">
            <div className="localops-region__section-eyebrow">JG LocalOps Camden launch</div>
            <h3>Need local help in Camden?</h3>
            <p>
              Use JG LocalOps in Camden to post local tasks, set up recurring help,
              or sponsor useful work.
            </p>
            <p className="localops-region__partner-urgency">
              The Camden launch is early. Early businesses and organizations can help
              shape the local work network as trusted helper capacity grows.
            </p>
            <div className="localops-region__partner-actions">
              <button
                type="button"
                className="localops-region__partner-action"
                onClick={handlePartnerPostTask}
              >
                <span>Post a task now</span>
                <small>For errands, pickups, cleanup, moving help, or event support.</small>
              </button>
              <button
                type="button"
                className="localops-region__partner-action"
                onClick={() => openPartnerLeadForm(PARTNER_ACTIONS.RECURRING_HELP)}
              >
                <span>Set up recurring local help</span>
                <small>
                  For repeated errands, pickups, cleanup, event support, or operations help.
                </small>
              </button>
              <button
                type="button"
                className="localops-region__partner-action"
                onClick={() =>
                  openPartnerLeadForm(PARTNER_ACTIONS.SPONSOR_OPPORTUNITIES)
                }
              >
                <span>Sponsor local opportunities</span>
                <small>
                  Fund useful local work, create Camden opportunities, and get proof
                  that work was completed.
                </small>
              </button>
            </div>
            {renderPartnerLeadForm()}
          </div>
        ) : null}

        {selectedIntent === INTENTS.EXPLORE ? (
          <div className="localops-region__explore" data-testid="offline-region-explore-surface">
            <div className="localops-region__section-eyebrow">
              Explore JG LocalOps in Camden
            </div>
            <h3>How JG LocalOps works in Camden</h3>
            <p>
              People and organizations can post local tasks. Nearby helpers can
              accept paid work when tasks are available.
            </p>
            <p className="localops-region__note" style={{ marginTop: 8 }}>
              Browse first. Sign in only when you're ready to take action.
            </p>
            <Link
              className="localops-region__context-link"
              to="/localops/how-it-works"
            >
              How does it work?
            </Link>
            {examples.length > 0 ? (
              <div className="localops-region__proof-block">
                <div className="localops-region__proof-title">Common local task ideas</div>
                <ul className="localops-region__proof-list">
                  {examples.slice(0, 3).map((example) => (
                    <li key={`proof-${example}`}>{example}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="localops-region__explore-actions">
              <button
                type="button"
                className="localops-region__intent localops-region__intent--earn"
                onClick={() => handleIntentChoice(INTENTS.EARN)}
              >
                See local tasks
              </button>
            </div>
            <form onSubmit={handleExploreSubmit}>
              <label htmlFor="explore-interest" className="localops-region__field-label">
                What kind of local tasks would you use or help with? (Optional)
              </label>
              <textarea
                id="explore-interest"
                className="localops-region__textarea"
                value={exploreInterest}
                onChange={(e) => setExploreInterest(e.target.value)}
                placeholder="Examples: grocery runs, moving help, errands"
              />
              <label htmlFor="explore-email" className="localops-region__field-label">
                Email for updates (required only if you submit)
              </label>
              <input
                id="explore-email"
                type="email"
                className="localops-region__input"
                value={exploreEmail}
                onChange={(e) => {
                  setExploreEmail(e.target.value);
                  if (exploreEmailError) setExploreEmailError('');
                }}
                placeholder="you@example.com"
              />
              {exploreEmailError ? (
                <div
                  style={{
                    marginTop: 8,
                    color: '#ff9b9b',
                    fontSize: 12,
                  }}
                >
                  {exploreEmailError}
                </div>
              ) : null}
              <button
                type="submit"
                className="localops-region__intent localops-region__intent--explore"
                data-testid="offline-region-explore-submit"
                disabled={exploreSubmitStatus === 'submitting'}
                aria-busy={exploreSubmitStatus === 'submitting'}
                style={{ marginTop: 12, textAlign: 'center' }}
              >
                {exploreSubmitStatus === 'submitting'
                  ? 'Requesting Camden launch updates'
                  : 'Request Camden launch updates'}
              </button>
              {exploreSubmitMessage ? (
                <div
                  className="localops-region__note"
                  data-testid="offline-region-explore-submit-status"
                  data-status={exploreSubmitStatus}
                  style={{ marginTop: 10 }}
                >
                  {exploreSubmitMessage}
                </div>
              ) : null}
              <div className="localops-region__note localops-region__note--secondary">
                No signup is required to keep exploring.
              </div>
            </form>
          </div>
        ) : null}

        {visibleSpotlight && !isCollisionSpecialistRide ? (
          <section
            className="localops-region__spotlight"
            data-testid="localops-business-spotlight"
            data-spotlight-count={spotlightCards.length}
          >
            <div className="localops-region__spotlight-header">
              <div className="localops-region__spotlight-label">
                <div className="localops-region__section-eyebrow">
                  JG Local Business Spotlight
                </div>
                <span className="localops-region__spotlight-disclosure">Ad</span>
              </div>
              <a
                className="localops-region__spotlight-advertise-link"
                href={buildSpotlightAdvertisePath(slug)}
              >
                Advertise with us
              </a>
            </div>
            <div className="localops-region__spotlight-grid">
              <article
                key={`${visibleSpotlight.sponsorName}-${visibleSpotlight.headline}`}
                className="localops-region__spotlight-card"
              >
                <SpotlightImage spotlight={visibleSpotlight} />
                <div className="localops-region__spotlight-body">
                  {visibleSpotlight.businessCategory ? (
                    <div className="localops-region__spotlight-category">
                      {visibleSpotlight.businessCategory}
                    </div>
                  ) : null}
                  <h2 className="localops-region__spotlight-sponsor">
                    {visibleSpotlight.sponsorName}
                  </h2>
                  {visibleSpotlight.headline ? (
                    <p className="localops-region__spotlight-headline">
                      {visibleSpotlight.headline}
                    </p>
                  ) : null}
                  <p
                    className={[
                      'localops-region__spotlight-offer',
                      visibleSpotlight.offer.length > 60
                        ? 'localops-region__spotlight-offer--long'
                        : 'localops-region__spotlight-offer--short',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {visibleSpotlight.offer}
                  </p>
                </div>
                {visibleSpotlight.destinationUrl ? (
                  <a
                    href={visibleSpotlight.destinationUrl}
                    className="localops-region__spotlight-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleSpotlightClick(visibleSpotlight)}
                  >
                    {visibleSpotlight.ctaLabel}
                  </a>
                ) : null}
              </article>
            </div>
          </section>
        ) : null}

        {!isCollisionSpecialistRide ? (
          <div
            className="localops-region__examples"
            data-testid="localops-common-uses"
          >
            <div className="localops-region__examples-label">Common local uses</div>
            <div className="localops-region__examples-list">
              {PUBLIC_LAUNCH_USE_ROWS.map((example) => (
                <div key={example} className="localops-region__example">
                  {example}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {proofReceipts.length > 0 && !isCollisionSpecialistRide ? (
          <section
            className="localops-region__proof-receipts"
            data-testid="localops-featured-proof-receipts"
          >
            <div className="localops-region__section-eyebrow">
              Action Proof Receipts
            </div>
            <h2>Completed in Camden - with proof</h2>
            <p>
              Real Action Proof Receipts from local pickups, rides, errands,
              and Camden helper work.
            </p>
            <div className="localops-region__proof-receipt-grid">
              {proofReceipts.map((proof) => (
                <article
                  className="localops-region__proof-receipt-card"
                  key={proof.proofId || proof.proofUrl}
                >
                  <div className="localops-region__proof-receipt-badge">
                    Verified completion
                  </div>
                  <h3>{proof.title}</h3>
                  <div className="localops-region__proof-receipt-meta">
                    <span>{proof.categoryLabel}</span>
                    {proof.completedAt ? (
                      <time dateTime={proof.completedAt}>
                        {formatProofDate(proof.completedAt)}
                      </time>
                    ) : null}
                  </div>
                  {proof.shortSummary ? <p>{proof.shortSummary}</p> : null}
                  <button
                    className="localops-region__proof-receipt-link"
                    type="button"
                    onClick={(event) => handleProofPreviewClick(proof, event)}
                  >
                    Preview proof
                  </button>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {previewProof ? (
          <div
            className="localops-region__proof-preview-backdrop"
            onMouseDown={handleProofPreviewBackdropMouseDown}
          >
            <section
              aria-labelledby="localops-proof-preview-title"
              aria-modal="true"
              className="localops-region__proof-preview"
              role="dialog"
            >
              <button
                aria-label="Close proof preview"
                className="localops-region__proof-preview-close"
                onClick={closeProofPreview}
                ref={proofPreviewCloseRef}
                type="button"
              >
                X
              </button>
              <div className="localops-region__proof-receipt-badge">
                Verified completion
              </div>
              <h2 id="localops-proof-preview-title">Action Proof Receipt</h2>
              <h3>{previewProof.title}</h3>
              <div className="localops-region__proof-preview-meta">
                <span>{previewProof.categoryLabel}</span>
                {previewProof.completedAt ? (
                  <time dateTime={previewProof.completedAt}>
                    {formatProofDate(previewProof.completedAt)}
                  </time>
                ) : null}
              </div>
              {previewProof.shortSummary ? (
                <p>{previewProof.shortSummary}</p>
              ) : null}
              <p>
                This is a public proof receipt from a completed Camden LocalOps
                task.
              </p>
              <div className="localops-region__proof-preview-actions">
                <button
                  className="localops-region__proof-preview-primary"
                  onClick={handleProofPostLikeThisClick}
                  type="button"
                >
                  Need help like this? Post a task
                </button>
                <a
                  className="localops-region__proof-preview-secondary"
                  href={previewProof.proofUrl}
                  onClick={handleProofFullReceiptClick}
                  rel="noopener noreferrer"
                  target="_blank"
                  aria-label="Open full proof receipt in a new tab"
                >
                  Open full proof receipt {'\u2197'}
                </a>
                <button
                  className="localops-region__proof-preview-tertiary"
                  onClick={handleProofEarnClick}
                  type="button"
                >
                  Want to earn by helping?
                </button>
              </div>
            </section>
          </div>
        ) : null}

        {!isCollisionSpecialistRide ? (
          <section
            className="localops-region__how"
            data-testid="localops-region-how-it-works"
          >
            <div className="localops-region__section-eyebrow">Local flow</div>
            <h2>How LocalOps works</h2>
            <ol>
              {HOW_LOCALOPS_WORKS_STEPS.map((step) => (
                <li key={step.title}>
                  <strong>{step.title}</strong>
                  <span>{step.copy}</span>
                </li>
              ))}
            </ol>
            <p>
              Helpers build trust through verified completions and Action
              Credit.
            </p>
          </section>
        ) : null}

      </div>
      </div>
    </>
  );
}
